import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Lets the shopper buy the product alone or one of the bundle options (product + add-ons).
 * When a bundle option is selected, the product form's add to cart click is
 * cancelled and all items of that option are added to the cart in one request instead.
 *
 * @typedef {object} AkBundleOfferRefs
 * @property {HTMLElement} error - The error message element.
 *
 * @extends Component<AkBundleOfferRefs>
 */
class AkBundleOfferComponent extends Component {
  requiredRefs = ['error'];
  #abortController = new AbortController();
  #adding = false;

  /** @type {HTMLInputElement | null} Bundle option that was already selected when pressed */
  #pressedSelected = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    // Capture phase, so the click is cancelled before the form submits. The sticky
    // add to cart bar clicks the same button, so it is covered too.
    document.addEventListener('click', this.#onClick, { capture: true, signal });
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('click', this.#onOptionClick, { signal });

    this.#ensureSelection();
    this.#syncProductForm();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#productForm?.removeAttribute('data-ak-bundle-selected');
  }

  handleChange() {
    this.#syncProductForm();
  }

  get #selectedInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('.ak-bundle-offer__radio:checked'));
  }

  get #bundleSelected() {
    const value = this.#selectedInput?.value;
    return Boolean(value) && value !== 'single';
  }

  /** Picks the default option when nothing is checked (e.g. after the theme editor re-renders the block). */
  #ensureSelection() {
    if (this.#selectedInput) return;

    const firstBundle = this.querySelector('.ak-bundle-offer__radio[value^="bundle-"]');
    const single = this.querySelector('.ak-bundle-offer__radio[value="single"]');
    const input = this.dataset.defaultOption === 'bundle' ? firstBundle || single : single;
    if (input instanceof HTMLInputElement) input.checked = true;
  }

  get #form() {
    const form = document.getElementById(this.dataset.formId ?? '');
    return form instanceof HTMLFormElement ? form : null;
  }

  get #productForm() {
    return this.#form?.closest('product-form-component') ?? null;
  }

  #syncProductForm() {
    this.#productForm?.toggleAttribute('data-ak-bundle-selected', this.#bundleSelected);
  }

  /**
   * Radios can't be unselected by clicking them again, so remember whether the pressed
   * bundle option was already selected; the click then switches back to "this product only".
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    const option = event.target instanceof Element ? event.target.closest('.ak-bundle-offer__option--bundle') : null;
    const radio = option?.querySelector('.ak-bundle-offer__radio');
    this.#pressedSelected = radio instanceof HTMLInputElement && radio.checked ? radio : null;
  };

  /** @param {MouseEvent} event */
  #onOptionClick = (event) => {
    const radio = this.#pressedSelected;
    if (!radio || !(event.target instanceof Element) || !radio.closest('.ak-bundle-offer__option')?.contains(event.target)) return;
    this.#pressedSelected = null;

    // After the browser has finished the label/radio click, which keeps the option checked.
    setTimeout(() => {
      const single = this.querySelector('.ak-bundle-offer__radio[value="single"]');
      if (single instanceof HTMLInputElement) {
        single.checked = true;
      } else {
        radio.checked = false;
      }
      this.#syncProductForm();
    });
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#bundleSelected) return;

    const form = this.#form;
    if (!form || !(event.target instanceof Element)) return;

    // "Buy it now" (accelerated checkout) only knows the main product: add the whole bundle
    // to the cart instead, then go to checkout. Capture phase runs before the button's own handler.
    const buyNow = this.#productForm?.querySelector('.accelerated-checkout-block');
    if (buyNow?.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      this.#addBundle(form, { checkout: true });
      return;
    }

    const button = event.target.closest('button');
    if (!button || button.type !== 'submit' || button.form !== form) return;
    if (!form.checkValidity()) return;

    event.preventDefault();
    this.#addBundle(form);
  };

  /**
   * @param {HTMLFormElement} form
   * @param {{ checkout?: boolean }} [options] - `checkout` goes to checkout after adding
   */
  async #addBundle(form, { checkout = false } = {}) {
    if (this.#adding) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) return;

    this.#adding = true;
    this.#hideError();

    // Keep the main product's quantity from the form (e.g. set by a quantity app), then add the
    // option's items. Same variants are merged ("+1 kit" adds to the kit already in the request).
    const quantities = new Map([[Number(variantId), Number(formData.get('quantity')) || 1]]);
    const option = this.#selectedInput?.closest('.ak-bundle-offer__option');
    for (const item of option?.querySelectorAll('[data-variant-id]') ?? []) {
      const { variantId: id, quantity } = /** @type {HTMLElement} */ (item).dataset;
      quantities.set(Number(id), (quantities.get(Number(id)) ?? 0) + (Number(quantity) || 1));
    }
    const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        this.#showError(data.description || data.message);
        return;
      }

      if (checkout) {
        window.location.assign('/checkout');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-form-component',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showError(this.dataset.errorMessage);
    } finally {
      this.#adding = false;
    }
  }

  /**
   * Re-renders prices for the newly selected variant, keeping the shopper's choice.
   * @param {import('./events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (!event.detail.data.newProduct && event.detail.data.productId !== this.dataset.productId) return;

    const newBlock = event.detail.data.html?.querySelector(`ak-bundle-offer[data-block-id="${this.dataset.blockId}"]`);
    if (!newBlock) return;

    const selectedValue = this.#selectedInput?.value;
    morph(this, newBlock);

    const selected = this.querySelector(`.ak-bundle-offer__radio[value="${selectedValue}"]`);
    if (selected instanceof HTMLInputElement) selected.checked = true;
    this.#ensureSelection();
    this.#syncProductForm();
  };

  /** @param {string | undefined} message */
  #showError(message) {
    this.refs.error.textContent = message ?? '';
    this.refs.error.hidden = !message;
  }

  #hideError() {
    this.refs.error.textContent = '';
    this.refs.error.hidden = true;
  }
}

if (!customElements.get('ak-bundle-offer')) {
  customElements.define('ak-bundle-offer', AkBundleOfferComponent);
}
